import { Download, ExternalLink, Smartphone, Usb } from "lucide-react";
import { getBikeById } from "../lib/bikes";

const ZFLASHER_PLAY_STORE_URL =
    "https://play.google.com/store/apps/details?id=ru.zdevs.zflasheravr";

export const AndroidFlashingGuide = ({ selectedBikeId, firmwareReady }) => {
    const bike = getBikeById(selectedBikeId);
    const firmwareUrl = bike
        ? `${process.env.PUBLIC_URL || ""}/firmware/${bike.file}`
        : null;

    return (
        <section
            data-testid="android-flashing-guide"
            className="border border-[#F59E0B]/50 bg-[#F59E0B]/5 p-5 md:p-6"
        >
            <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center border border-[#F59E0B]/50 bg-[#F59E0B]/10 shrink-0">
                    <Smartphone className="w-5 h-5 text-[#F59E0B]" />
                </div>
                <div>
                    <h2 className="text-sm font-bold text-[#ededed]">
                        Flash from your Android phone
                    </h2>
                    <p className="text-xs text-[#a1a1aa] mt-1 leading-relaxed">
                        Use ZFlasher AVR with a USB OTG adapter to install the
                        firmware.
                    </p>
                </div>
            </div>

            <ol className="mt-5 flex flex-col gap-4">
                <li className="flex gap-3">
                    <span className="text-xs font-mono text-[#F59E0B]">01</span>
                    <div className="flex-1">
                        <div className="text-sm text-[#ededed]">Install ZFlasher AVR</div>
                        <a
                            data-testid="zflasher-download-link"
                            href={ZFLASHER_PLAY_STORE_URL}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-2 w-full py-3 px-4 bg-[#F59E0B] text-[#0d0d0e] text-xs font-bold uppercase tracking-[0.12em] flex items-center justify-center gap-2 hover:brightness-110 transition-all"
                        >
                            <Download className="w-4 h-4" />
                            Get ZFlasher on Google Play
                            <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                    </div>
                </li>

                <li className="flex gap-3">
                    <span className="text-xs font-mono text-[#F59E0B]">02</span>
                    <div className="text-sm text-[#ededed] leading-relaxed">
                        Open ZFlasher and set the MCU to
                        <span className="block mt-2 px-3 py-2 border border-[#27272a] bg-[#0d0d0e] font-mono text-[#F59E0B]">
                            ATmega328P
                        </span>
                    </div>
                </li>

                <li className="flex gap-3">
                    <span className="text-xs font-mono text-[#F59E0B]">03</span>
                    <div className="flex-1">
                        <div className="text-sm text-[#ededed]">
                            Download your bike's HEX file, then select it in
                            ZFlasher.
                        </div>
                        {bike && firmwareReady ? (
                            <a
                                data-testid="android-firmware-download"
                                href={firmwareUrl}
                                download={bike.file}
                                className="mt-2 w-full py-3 px-4 border border-[#F59E0B] text-[#F59E0B] text-xs font-bold uppercase tracking-[0.12em] flex items-center justify-center gap-2 hover:bg-[#F59E0B]/10 transition-all"
                            >
                                <Download className="w-4 h-4" />
                                Download {bike.name} HEX
                            </a>
                        ) : (
                            <div className="mt-2 px-3 py-3 border border-[#27272a] bg-[#0d0d0e] text-xs font-mono text-[#a1a1aa]">
                                Select your bike above to enable the download.
                            </div>
                        )}
                    </div>
                </li>

                <li className="flex gap-3">
                    <span className="text-xs font-mono text-[#F59E0B]">04</span>
                    <div className="text-sm text-[#ededed] leading-relaxed">
                        <span className="flex items-center gap-2">
                            <Usb className="w-4 h-4 text-[#F59E0B]" />
                            Connect the module using USB OTG, then tap Flash in
                            ZFlasher.
                        </span>
                    </div>
                </li>
            </ol>

            <div className="mt-5 pt-5 border-t border-[#27272a]">
                <div className="text-[10px] font-mono text-[#a1a1aa] uppercase tracking-[0.15em] mb-3">
                    Android flashing video guide
                </div>
                <div className="relative w-full overflow-hidden border border-[#27272a] bg-black aspect-video">
                    <iframe
                        data-testid="android-video-guide"
                        className="absolute inset-0 w-full h-full"
                        src="https://www.youtube-nocookie.com/embed/G5E21xY1dyM"
                        title="How to flash Hold My Throttle firmware on Android"
                        loading="lazy"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                    />
                </div>
            </div>

            <p className="mt-5 pt-4 border-t border-[#27272a] text-[10px] font-mono text-[#a1a1aa] leading-relaxed uppercase tracking-[0.1em]">
                Keep the phone connected until flashing is complete. Your phone
                must support USB OTG.
            </p>
        </section>
    );
};
